<template>
  <div
    id="app"
    class="app"
    :class="{ 'hide-cursor': isTerminalChannel && !isTerminalMouseCursorEnabled }"
    :style="getBgImage"
  >
    <TicketHistory />
    <Modal
      v-if="ticketCanceled"
      ref="modal"
      show-footer
      class="cancel-ticket-confirm-modal"
      html-id="cancel-confirm-modal"
      @close-modal="closeCancelTicketModal"
      @close-modal-outside="closeCancelTicketModal('outside')"
    >
      >
      <template #header>
        <h3>
          {{ translations.general_info }}
        </h3>
      </template>
      <template #body>
        <div>
          {{ ticketCanceledMessage }}
          <div class="modal-actions">
            <button class="button" @click="closeCancelTicketModal">
              {{ translations.general_ok }}
            </button>
          </div>
        </div>
      </template>
      <template #footer>
        <div v-if="!isMobile">
          <button class="button" @click="closeCancelTicketModal">
            {{ translations.general_ok }}
          </button>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="showFreeBetModal"
      ref="modal"
      class="free-bet-modal"
      :class="[{ mobile: isMob }, { desktop: isDesktop }]"
      width="344px"
      height="348px"
      show-footer
      @close-modal="claimFreeBets()"
      @close-modal-outside="claimFreeBets('outside')"
    >
      <template #body>
        <div class="body">
          <img src="./assets/free-bet-star.png" alt="" />
          <div class="body-message-box">
            <div class="pre-title">{{ translations.general_free_bet_pre_title.toUpperCase() }}</div>
            <div class="title">{{ translations.general_free_bet }}</div>
            <div class="subtitle">{{ translations.general_congratulation.toUpperCase() }}</div>
            <span class="subtitle-2">{{ translations.general_claim_ticket }}</span>
          </div>
          <div class="action">
            <button class="button" @click="claimFreeBets">
              {{ claimFreeBetsLabel }}
            </button>
          </div>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="showFreeBetExpiredModal"
      ref="modal"
      class="free-bet-expired-modal"
      :class="[{ mobile: isMobile }, { desktop: isDesktop }]"
      width="344px"
      height="348px"
      show-footer
      @close-modal="toggleFreeBetExpiredModal(false)"
      @close-modal-outside="toggleFreeBetExpiredModal('outside')"
    >
      <template #header>
        <h3>
          {{ translations.general_info }}
        </h3>
      </template>
      <template #body>
        <div>
          {{ freeBetExpiredMessage }}
        </div>
      </template>
      <template #footer>
        <div>
          <button class="button" @click="toggleFreeBetExpiredModal(false)">
            {{ translations.general_ok }}
          </button>
        </div>
      </template>
    </Modal>
    <router-view></router-view>
    <CircleLoader :loader="isTerminalLayout && !loaded" />
    <LandscapeNotification />
  </div>
</template>

<script>
import { merge, each, assign, startCase, upperFirst } from 'lodash';
import { mapState, mapActions, mapGetters } from 'vuex';
import applyPalette from '@nsftx/games-sdk-js/src/utility/applyPalette';
import gtag from '@nsftx/games-sdk-js/src/utility/gtag';
import CircleLoader from '@nsftx/games-sdk-js/src/components/CircleLoader';
import LandscapeNotification from '@nsftx/games-sdk-js/src/components/LandscapeNotification';
import Modal from '@nsftx/games-sdk-js/src/components/Modal';
import { eventBus } from '@nsftx/games-sdk-js/src/utility';
import { BusService } from '@nsftx/games-bus';
import TicketHistory from '@/components/history/TicketHistory';
import DataService from '@/service/data';
import EventService from '@/service/event';
import NoSleep from 'nosleep.js';
import { ulid } from 'ulid';

export default {
  name: 'App',
  components: {
    TicketHistory,
    Modal,
    CircleLoader,
    LandscapeNotification,
  },
  data() {
    return {
      busService: null,
      isLoggedIn: false,
      checkTimeout: 10000,
      minimimBetAmout: null,
      ticketCanceled: false,
      canceledTicket: {},
      markets: {
        1: { label: 'betting.field_betting', odd: null },
        2: { label: 'betting.odd_or_even', odd: null },
        3: { label: 'betting.purple_or_yellow', odd: null },
        4: { label: 'betting.row_betting', odd: null },
        5: { label: 'betting.column_betting', odd: null },
        6: { label: 'betting.left_or_right', odd: null },
        7: { label: 'betting.no_goal', odd: null },
      },
      terminalPayinTicket: null,
      userFreeBets: {
        count: 0,
        betStake: 0,
      },
    };
  },
  computed: {
    ...mapState([
      'config',
      'loaded',
      'currentRound',
      'userUuid',
      'terminalUserData',
      'ticketDetailsData',
      'isTerminalMouseCursorEnabled',
      'activeTerminalLayout',
      'jackpotState',
      'showFreeBetModal',
      'showFreeBetExpiredModal',
      'nextgenStandaloneIntegration',
    ]),
    ...mapGetters([
      'user/user',
      'translations',
      'isIsonis',
      'isTerminalLayout',
      'isTerminalChannel',
      'ajsDisabled',
      'isDesktop',
      'isMobile',
      'customDomain',
      'isNextGen',
      'gameTitle',
    ]),
    ...mapGetters({
      isUserLoggedIn: 'user/isLoggedIn',
    }),
    ...mapGetters('gamesBetslip', ['ticket']),
    ticketCanceledMessage() {
      return `${this.translations.general_ticket} ${this.canceledTicket.id} ${this.translations.general_successfully_cancelled}!`;
    },
    userProfile() {
      return this['user/user'];
    },
    getTicketDetailsAuth() {
      return btoa(`${this.config.user.id}:${this.config.account.password}`);
    },
    getBgImage() {
      if (this.config.appLayout === 'TerminalTicketDetails' || this.isMobile) {
        return 'background-color: var(--background-1);';
      }
      return this.config.ui.dark ? this.bgImage : this.bgImageLight;
    },
    bgImage() {
      return {
        backgroundImage: `linear-gradient(to bottom, rgba(26, 28, 29, 0), #1A1C1D 88%), url(${require('@/assets/' + this.config.productName + '_dark_bg.jpg')})`,
      };
    },
    bgImageLight() {
      return {
        backgroundImage: `linear-gradient(to bottom, rgba(26, 28, 29, 0), #FFFFFF 88%), url(${require('@/assets/' + this.config.productName + '_light_bg.jpg')})`,
      };
    },
    getTicketHistoryAuthHeaders() {
      const headers = {
        Authorization: `Bearer ${this.terminalUserData.auth.token}`,
        'HTTP-X-NAB-BETSHOP-ID': this.config.betshop.id,
        'HTTP-X-NAB-COMPANY-ID': this.config.companyData.id,
        'HTTP-X-NAB-DEVICE-UUID': this.config.device.deviceUuid,
        'HTTP-X-NAB-DP': 'AdminTerminal',
        'SEVEN-LOCALE': this.config.locale,
      };
      return headers;
    },
    isStandAlone() {
      return this.config.ui.config.mode === 'standalone';
    },
    isGravityGateway() {
      return this.config.messageProtocol.name === 'gravityGateway';
    },
    claimFreeBetsLabel() {
      if (this.isMobile) {
        return this.translations.general_claim;
      }
      return this.translations.general_ok;
    },
    freeBetExpiredMessage() {
      return this.translations.general_free_bet_expired_message;
    },
    userLogged() {
      return this.userProfile.profile.logged || this.isLoggedIn || this.isUserLoggedIn;
    },
  },
  watch: {
    isUserLoggedIn(value) {
      if (value) {
        this.getLastTickets();
      }
    },
  },
  mounted() {
    if (!this.isDesktop) {
      const noSleep = new NoSleep();
      let wakeLockEnabled = false;
      document.addEventListener(
        'click',
        function enableNoSleep() {
          document.removeEventListener('click', enableNoSleep, false);
          if (!wakeLockEnabled) {
            noSleep.enable();
            wakeLockEnabled = true;
          } else {
            noSleep.disable();
            wakeLockEnabled = false;
          }
        },
        false,
      );
    }
    this.sendLoadedEvent();
    this.setTheme(this.config);
    this.setPageTitle();
    window.addEventListener('Bus:Message', async (event) => {
      const { eventName } = event.detail;
      switch (eventName) {
        case 'SetState':
          this.handleSetState(event.detail);
          break;
        case 'Tickets.FetchHistory':
          this.handleFetchHistory();
          break;
        case 'StartEvent':
          this.handleStartEvent(event.detail);
          break;
        case 'StartCountdown':
          this.handleStartCountDown(event.detail);
          break;
        case 'SetResults':
          this.handleSetResults(event.detail);
          break;
        case 'TicketUpdate': {
          this.handleTicketUpdate(event.detail);
          break;
        }
        case 'User.AuthorizationChanged':
          this.handleAuthorizationChanged(event.detail);
          break;
        case 'User.SettingsChanged':
          if (!this.isLoggedIn) {
            this.resetUserFreeBets();
            this.clearBetslip();
            this.setUserFreeBets(this.userFreeBets);
          } else {
            this.getUserFreeBets();
          }
          break;
        case 'User.BalanceChanged':
          this.setUserBalance(event.detail.data.balance);
          break;
        case 'Tickets.Checked':
          this.handleTicketsChecked(event.detail);
          break;
        case 'FreeBet':
        case 'freebet':
          this.userFreeBets = event.detail;
          if (event.detail.subtype === 'new') {
            this.toggleFreeBetModal(true);
          } else {
            if (this.userProfile.freeBets.count > 0) {
              this.toggleFreeBetExpiredModal(true);
            }
            this.getUserFreeBets();
          }
          break;
        case 'UI.Show': {
          this.setActiveTerminalLayout(event.detail.data.name[0]);
          break;
        }
        // IGNITE
        case 'onSettingsUpdated':
          if (this.config.locale !== event.detail.data.activeLocale) {
            this.updateConfig({ locale: event.detail.data.activeLocale });
            // TODO HANDLE TRANSLATIONS CHANGE
          }
          if (this.config.currency !== event.detail.data.activeCurrencyBalance) {
            this.updateConfig({ currency: event.detail.data.activeCurrency.code });
          }
          break;
        case 'onBettingDenied': //* TODO
          this.disableAllBetting(true);
          this.setIsPayinButtonDisabled(true);
          this.clearAndSetNotification({
            message: this.translations.betting_disabled,
            status: 'negative',
          });
          break;
        case 'onBettingAllowed': //* TODO
          this.disableAllBetting(false);
          this.setIsPayinButtonDisabled(false);
          this.clearNotification();
          break;
        case 'onCancelBetsRequested':
          // eslint-disable-next-line no-console
          console.log('CANCEL BETS REQUESTED:', event.detail.data);
          this.cancelTicket({
            id: event.detail.data.betIds[0],
            providerBetslipId: event.detail.data.providerBetslipId,
          });
          break;
        case 'onReBetRequested':
          // eslint-disable-next-line no-console
          console.log('REBET REQUESTED:', event.detail.data);
          // eslint-disable-next-line no-console
          console.warn('Rebet not possible on this game');
          break;
        default:
          break;
      }
    });

    eventBus.$on('LoginRequired', () => {
      if (window.WebAppListener) {
        window.WebAppListener.sendMessage('User.LoginRequired');
      }
      this.busService.sendMessage(
        {
          action: 'User.LoginRequired',
        },
        true,
      );
    });

    eventBus.$on('BetRemoved', () => {
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      window.dispatchEvent(clickEvent);
    });

    eventBus.$on('TicketPayin', async () => {
      if (!this.isTerminalChannel) {
        this.disable_betting(true);
        this.setScheduleDisabled(true);
        const additionalInfo = {
          printTemplate: this.getPrintTemplate(),
          timezone: {
            offset: parseInt(this.userProfile.profile.timezoneOffset),
          },
        };
        this.setIsPayinButtonDisabled(true);
        this.setIsPayinInProgress(true);
        try {
          const response = await this.payIn({
            payload: this.formatTicketBets,
            additionalInfo: this.isIsonis ? additionalInfo : null,
            providerBetslipId: ulid() || null,
          });
          // IGNITE IMPLEMENTATION
          if (this.isNextGen && response.status === 201) {
            this.setIsPayinInProgress(false);
            this.setIsPayinButtonDisabled(false);
            this.disable_betting(false);
            this.setScheduleDisabled(false);
            const message = {
              message: this.translations.general_ticket_confirmed,
              status: 'positive',
              notificationTimeout: 3000,
            };
            await this.clearBetslip();
            this.clearAndSetNotification(message);
            this.getLastTickets();
            this.getUserFreeBets();
            if (this.nextgenStandaloneIntegration) {
              await window.igniteInstance.getWallets().then((result) => {
                if (result.activeCurrencyBalance) {
                  this.setUserBalance(result.activeCurrencyBalance);
                }
              });
            }
          } else {
            // seven
            this.checkTicket(response.data.requestUuid);
            const message = {
              message: this.translations.general_ticket_resolving,
              status: 'neutral',
            };
            this.setNotification(message);
          }
        } catch (error) {
          if (error) {
            const message = {
              message: this.translations.general_ticket_error,
              status: 'negative',
            };
            this.setNotification(message);
            this.setIsPayinInProgress(false);
            this.disable_betting(false);
            this.setScheduleDisabled(false);
            this.setIsPayinButtonDisabled(false);
          }
          if (error.response) {
            const errorData = error.response.data;
            // eslint-disable-next-line no-console
            console.error(errorData);
            const message = {
              message: error.response.data.message
                ? error.response.data.message
                : this.translations.general_ticket_error,
              status: 'negative',
            };
            if (error.response.data.code === 11000) {
              message.message = this.translations.general_not_verified_email;
            }
            this.setIsPayinInProgress(false);
            this.setNotification(message);
          }
          this.setIsPayinButtonDisabled(false);
        }
      }
    });

    eventBus.$on('ReturnToLobby', () => {
      if (window.WebAppListener) {
        window.WebAppListener.sendMessage('Navigation.Back');
      }
      this.busService.sendMessage(
        {
          action: 'UI.Show',
          data: {
            name: 'Lobby',
          },
        },
        true,
      );
    });

    eventBus.$on('RemoveInvalidBets', () => {
      this.clearNotification();
    });

    eventBus.$on('PayoutTicket', (ticket) => {
      this.busService.sendMessage(
        {
          action: 'Tickets.Payout',
          data: {
            ticket,
          },
        },
        true,
      );
    });

    eventBus.$on('LoadLastTickets', () => {
      this.getLastTickets();
    });

    eventBus.$on('PrintTicketCopy', (ticket) => {
      const mappedTicket = assign(ticket, { isCopy: true }, { translation: this.gameTitle });
      each(mappedTicket.bets, (bet) => {
        bet.market = this.translations[this.getMarketLabel(bet.type)];
        bet.odd = this.markets[bet.type].odd;
      });
      this.busService.sendMessage(
        {
          action: 'Peripherals.Print',
          enforceEvent: true,
          data: {
            type: 'ticket',
            productId: 'VirtualPenaltyShootout',
            data: mappedTicket,
          },
        },
        true,
      );
    });

    eventBus.$on('TicketPayin', async () => {
      if (this.isTerminalChannel) {
        this.disable_betting(true);
        this.setScheduleDisabled(true);
        this.setIsPayinInProgress(true);
        this.setIsPayinButtonDisabled(true);
        const additionalInfo = {
          printTemplate: this.getPrintTemplate(),
          timezone: {
            offset: 1,
          },
        };
        try {
          const response = await this.payIn({
            payload: this.formatTicketBets,
            additionalInfo: this.isIsonis ? additionalInfo : null,
          });
          this.busService.clientAdapter.handler
            .emitAsync({
              action: 'Tickets.Pay',
              data: response,
              async: true,
            })
            .then((response2) => {
              this.terminalPayinTicket = response2;
              this.checkTicket(response2.ticket.requestUuid);
            });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }
    });

    eventBus.$on('VpsShootPhase', () => {
      eventBus.$emit('updateSchedule');
      this.disable_betting(false);
    });

    eventBus.$on('TicketCancel', (ticket) => {
      this.busService.sendMessage(
        {
          action: 'Tickets.Cancel',
          data: {
            ticket,
          },
        },
        true,
      );
    });

    eventBus.$on('UserBalanceValid', () => {
      if (this.isTerminalLayout) {
        this.busService.sendMessage(
          {
            action: 'Dialog.Close',
            data: {
              id: 'vps_insufficient_balance',
            },
          },
          true,
        );
      }
    });

    eventBus.$on('TicketCancelled', async (ticketId) => {
      this.ticketCanceled = true;
      this.canceledTicket = { id: ticketId };
      this.getLastTickets();
      if (this.nextgenStandaloneIntegration) {
        setTimeout(async () => {
          await window.igniteInstance.getWallets().then((result) => {
            if (result.activeCurrencyBalance) {
              this.setUserBalance(result.activeCurrencyBalance);
            }
          });
        }, 1000);
      }
    });

    if (this.config.platformConfig.clientType) {
      if (this.userProfile && this.userProfile?.profile?.id) {
        this.togglePlayerChannel(this.userProfile.profile.id);
      }
    }
  },
  beforeMount() {
    this.busService = new BusService({
      environment: this.config.environment,
      platform: this.config.platformName,
      productName: this.config.productName,
      productId: this.config.productId,
      tenantId: this.config.tenantId,
      locale: this.config.locale,
      applicationName: this.config.applicationName,
      subChannels: [],
      useCommonEventName: true,
      adapters: [{ name: 'GcmAdapter' }],
      clientMessageProtocol: this.config.messageProtocol,
      customDomain: this.customDomain,
    });
    const device = this.isMobile ? 'mobile' : 'desktop';
    this.busService.start();
    this.setGtag(device);
    this.minimimBetAmout = this.config.product.rules.minBetAmount.value;
    if (!this.ajsDisabled) {
      this.setJackpotState();
      if (this.isTerminalChannel) {
        this.busService.addChannel('Device', this.config.device.uuid);
      }
      if (this.jackpotState) {
        this.busService.addChannel('Jackpot', this.jackpotState.jackpotId);
      }
    }
  },
  methods: {
    ...mapActions([
      'set_nogoal_odds',
      'set_loaded',
      'setUserBalance',
      'setCurrentRound',
      'setCurrentScheduleRound',
      'setNextRoundTime',
      'setUserUuid',
      'disable_betting',
      'setInitSetStateDate',
      'setTicketHistoryTerminal',
      'setTicketDetailsData',
      'setBetslipLockMessage',
      'setInProgressRound',
      'setUserLoggedIn',
      'setTerminalUserData',
      'setJackpotState',
      'setAuthChanged',
      'toggleFreeBetModal',
      'updateConfig',
      'toggleFreeBetExpiredModal',
      'setActiveTerminalLayout',
      'disableAllBetting',
      'setUserData',
      'setBetslipLockMessage',
    ]),
    ...mapActions('gamesBetslip', [
      'setIsPayinInProgress',
      'cancelTicket',
      'clearBetslip',
      'setIsPayinButtonDisabled',
      'getLastTickets',
      'setPlayerTickets',
      'setScheduleDisabled',
      'payIn',
      'ticketCheck',
    ]),
    ...mapActions('user', ['getUserBalance', 'setUserFreeBets', 'getUserFreeBets', 'updateUser']),
    ...mapActions('notifications', ['setNotification', 'clearNotification']),
    sendLoadedEvent() {
      if (window.WebAppListener) {
        window.WebAppListener.sendMessage('Slave.Loaded');
      }
    },
    setOddsForTicketPrint(odds) {
      each(odds, (odd) => {
        this.markets[odd.type].odd = odd.value;
      });
    },
    setGtag(device) {
      if (this.config.ui.config.gtm) {
        const { code, auth, preview } = this.config.ui.config.gtm[device];
        gtag.setup(code, auth, preview);
        return;
      }
      gtag.setup();
    },
    getMarketLabel(type) {
      return this.markets[type].label;
    },
    closeCancelTicketModal() {
      this.ticketCanceled = false;
    },
    setTheme(config) {
      const rootElement = document.documentElement;
      const baseColor = config.ui[config.ui.config.baseColor];
      const theme = config.ui[config.ui.config.theme];
      applyPalette.set(merge(theme, baseColor), rootElement);
    },
    setUser(user) {
      if (user.user) {
        this.isLoggedIn = !!user.token || !!user?.id;
        this.setUserLoggedIn(!!user.token);
        this.setUserData(user.user);
        if (this.userLogged) {
          this.setUserUuid(user.user.id);
          this.togglePlayerChannel(this.userProfile.profile.id);
          this.updateUser({
            auth: {
              token: user.token,
              tpToken: user.tpToken,
            },
            profile: user.user,
          });
          //  if (window.WebAppListener) {
          //    this.getUserFreeBets();
          //  }
        }
      }
    },
    togglePlayerChannel(playerId) {
      if (playerId) {
        this.busService.addChannel(`${this.config.productName}.Player`, playerId);
      } else {
        this.busService.removeChannel(`${this.config.productName}.Player`, this.userProfile.profile.id);
      }
    },
    formatTicketBets() {
      return this.ticket.map((n) => ({
        payin: Number(n.stake),
        type: n.type,
        value: n.value,
        numEvents: 1,
        eventId: n.eventId,
        market: n.market,
      }));
    },
    clearCheckTimeout() {
      clearTimeout(this.check);
    },
    checkTicket(requestUuid) {
      this.check = setTimeout(() => {
        try {
          const response = this.ticketCheck(requestUuid);

          if (response.data.status.value.toUpperCase() !== 'PENDING') {
            const message = {
              message: this.translations.general_ticket_confirmed,
              status: 'positive',
              notificationTimeout: 3000,
            };
            if (!this.isTerminalChannel) {
              this.getLastTickets();
            }
            this.setIsPayinButtonDisabled(false);
            this.clearBetslip();
            this.clearAndSetNotification(message);
            this.setIsPayinButtonDisabled(false);
            this.clearBetslip();
            this.setIsPayinInProgress(false);
            if (this.isTerminalLayout) {
              this.setBetslipLockMessage('');
              this.disable_betting(false);
              this.setIsPayinButtonDisabled(false);
            }
          }
          this.clearCheckTimeout();
        } catch (error) {
          if (this.isTerminalLayout) {
            this.setIsPayinInProgress(false);
            this.setIsPayinButtonDisabled(false);
            this.busService.sendMessage(
              {
                action: 'Tickets.Update',
                enforceEvent: true,
                data: {
                  action: 'Add',
                  ticketData: this.terminalPayinTicket,
                  showMessage: false,
                },
              },
              true,
            );
            this.busService.sendMessage(
              {
                action: 'Dialog.Show',
                data: {
                  message: this.translations.general_ticket_error,
                  type: 'warning',
                  delay: 3000,
                },
              },
              true,
            );
          }
        }

        this.disable_betting(false);
        this.setScheduleDisabled(false);
      }, this.checkTimeout);
    },
    getPrintTemplate() {
      let template = '';
      const messages = this.translations;
      const { currency } = this.config;
      const tempTickets = JSON.parse(JSON.stringify(this.ticket));
      const printBets = merge(tempTickets, this.formatTicketBets());
      template += `${this.gameTitle};`;
      template += "{{ ticketDateTimeUTC | date('d.m.Y H:i:s', false) }};";
      template += `${messages.general_pay_in} {{ payin | number_format(2, '.', ',') }}${currency} `;
      template += `${messages.general_payout} {{ payout | number_format(2, '.', ',') }}${currency} `;

      return (
        template +
        printBets
          .map((bet) => {
            let body = '';

            for (let i = 0; i < bet.numEvents; i += 1) {
              body += `${bet.outcome} `;
              body += `${bet.market} `;
              body += `${bet.round} `;
              body += `${bet.odd.toFixed(2)} `;
              body += `${bet.payin} `;
            }
            return body;
          })
          .join('; ') +
        '{{ id }}'
      );
    },
    setPageTitle() {
      const { productFqn, applicationName } = this.config;
      document.title = `${startCase(productFqn)}: ${upperFirst(applicationName)}`;
    },
    claimFreeBets() {
      this.clearBetslip();
      this.setUserFreeBets(this.userFreeBets);
      this.toggleFreeBetModal(false);
    },
    resetUserFreeBets() {
      this.userFreeBets = {
        count: 0,
        betStake: 0,
      };
    },
    async clearAndSetNotification(notification) {
      await this.clearNotification();
      this.setNotification(notification);
    },
    handleSetState(eventDetail) {
      const { eventIdToday, odds, offer, type, eventId } = eventDetail;
      this.setInProgressRound(eventIdToday);
      if (odds && this.isTerminalLayout) {
        this.setOddsForTicketPrint(odds);
      }
      this.setInitSetStateDate(offer[0].startDate);
      this.setNextRoundTime(offer[0].startsAt);
      this.setCurrentScheduleRound(eventIdToday);
      if (type === 'countdown') {
        EventService.phaseCountdown(DataService.parseEvent(eventDetail));
      }
      if (type === 'results') {
        EventService.phaseResults(DataService.parseEvent(eventDetail));
      }
      this.set_nogoal_odds(odds[odds.length - 1].value);
      EventService.state(DataService.parseEvent(eventDetail, 'state'));
      this.setCurrentRound(eventIdToday);
      this.busService.sendMessage(
        {
          action: 'Slave.Loaded',
        },
        true,
      );
      this.set_loaded(true);

      if (eventId) {
        setTimeout(() => {
          if (this.userLogged) {
            this.getLastTickets();
            this.getUserFreeBets();
          }
        }, 600);
      }
    },
    handleFetchHistory() {
      fetch(
        `${process.env.VUE_APP_TERMINAL_TICKET_HISTORY_PATH}/${this.config.productId}.json?deviceUuid=${this.config.device.deviceUuid}&timeFrom=${event.detail.data.timeFrom}&timeTo=${event.detail.data.timeTo}&count=${event.detail.data.count}`,
        {
          headers: this.getTicketHistoryAuthHeaders,
        },
      )
        .then((response) => response.text())
        .then((text) => this.setTicketHistoryTerminal(JSON.parse(text)));
    },
    handleStartEvent(eventDetail) {
      const { eventIdToday } = eventDetail;
      if (this.isTerminalChannel && this.ticketDetailsData) {
        fetch(`${process.env.VUE_APP_TERMINAL_TICKET_CHECK_PATH}/${this.ticketDetailsData.id}.json`, {
          headers: {
            Authorization: `Basic ${this.getTicketDetailsAuth}`,
            'SEVEN-LOCALE': this.config.locale,
          },
        })
          .then((response) => response.text())
          .then((text) => this.setTicketDetailsData(JSON.parse(text)));
      }
      this.setInProgressRound(eventIdToday);
      this.setCurrentRound(this.currentRound);
      EventService.phaseShoot(DataService.parseEvent(eventDetail));
      this.set_loaded(true);
    },
    async handleStartCountDown(eventDetail) {
      const { offer, eventIdToday } = eventDetail;

      this.setScheduleDisabled(false);
      this.setNextRoundTime(offer[0].startsAt);
      this.setCurrentScheduleRound(eventIdToday);
      this.setCurrentRound(this.currentRound);
      EventService.phaseCountdown(DataService.parseEvent(eventDetail));
      eventBus.$emit('updateSchedule');
      this.set_loaded(true);
      if (!this.isTerminalChannel && this.userLogged) {
        this.getLastTickets();
        if (this.isStandAlone && !this.isGravityGateway) {
          this.getUserBalance();
        }
        if (this.nextgenStandaloneIntegration) {
          await window.igniteInstance.getWallets().then((result) => {
            if (result.activeCurrencyBalance) {
              this.setUserBalance(result.activeCurrencyBalance);
            }
          });
        }
      }
      if (this.isTerminalChannel && this.ticketDetailsData) {
        fetch(`${process.env.VUE_APP_TERMINAL_TICKET_CHECK_PATH}/${this.ticketDetailsData.id}.json`, {
          headers: {
            Authorization: `Basic ${this.getTicketDetailsAuth}`,
            'SEVEN-LOCALE': this.config.locale,
          },
        })
          .then((response) => response.text())
          .then((text) => this.setTicketDetailsData(JSON.parse(text)));
      }
    },
    handleSetResults(eventDetail) {
      EventService.phaseResults(DataService.parseEvent(eventDetail));
      this.set_loaded(true);
      if (!this.isTerminalChannel && this.userLogged) {
        setTimeout(async () => {
          if (this.isStandAlone && !this.isGravityGateway) {
            this.getUserBalance();
          }
          this.getLastTickets();
          if (this.nextgenStandaloneIntegration) {
            await window.igniteInstance.getWallets().then((result) => {
              if (result.activeCurrencyBalance) {
                this.setUserBalance(result.activeCurrencyBalance);
              }
            });
          }
        }, 2000);
      }
    },
    async handleTicketUpdate(eventDetail) {
      const { id, status, code, ticketPin, payout, cancelledAmount } = eventDetail;
      if (this.userLogged && !this.isTerminalChannel) {
        const message = {
          message: this.translations.general_ticket_confirmed,
          status: 'positive',
          notificationTimeout: 3000,
        };
        if (id) {
          if (status.id === '0') {
            //  this.getUserFreeBets();
            this.busService.sendMessage(
              {
                action: 'Tickets.Resolved',
                data: {
                  accepted: true,
                  ticket: message,
                },
              },
              true,
            );
          } else if (status.id === '1') {
            message.message = this.translations.general_ticket_canceled;
            message.status = 'neutral';
            this.canceledTicket = eventDetail;
            this.ticketCanceled = true;
            eventBus.$emit('RefreshTicketHistory');
          }
        }

        if (code) {
          const eventMessage = eventDetail.messageKeyName
            ? this.translations[eventDetail.messageKeyName]
            : eventDetail.message;
          message.message = eventMessage;
          message.status = 'negative';
          eventBus.$emit('SwitchToBetslipTab');
          this.clearAndSetNotification(message);
        } else {
          await this.clearBetslip();
          this.clearAndSetNotification(message);
        }
        this.getLastTickets();
        this.clearAndSetNotification(message);

        this.setIsPayinButtonDisabled(false);
        this.setIsPayinInProgress(false);
        this.clearCheckTimeout();
        this.disable_betting(false);
        this.setScheduleDisabled(false);
        if (this.isStandAlone && !this.isGravityGateway) {
          this.getUserBalance();
        }
      }
      if (this.isTerminalChannel) {
        const ticket = JSON.parse(JSON.stringify(eventDetail));
        each(ticket.bets, (bet) => {
          bet.market = this.translations[this.getMarketLabel(bet.type)];
          bet.odd = this.markets[bet.type].odd;
        });
        ticket.ticketPin = ticketPin;
        if (status.id === '0' && this.activeTerminalLayout === 'BettingArea') {
          ticket.translation = this.gameTitle;
          ticket.payinConfirmation = this.translations['print.payin_confirmation'];
          const additionalData = {
            clientPrintJobIdentifier: {
              id: 'TicketPayIn',
              uuid: ticket.requestUuid,
            },
          };
          this.busService.sendMessage(
            {
              action: 'Peripherals.Print',
              enforceEvent: true,
              data: {
                type: 'ticket',
                productId: 'VirtualPenaltyShootout',
                data: ticket,
                additionalData,
              },
            },
            true,
          );
          this.busService.sendMessage(
            {
              action: 'Tickets.Update',
              enforceEvent: true,
              data: {
                action: 'Add',
                ticketData: ticket,
                showMessage: false,
              },
            },
            true,
          );
          this.busService.sendMessage(
            {
              action: 'Dialog.Show',
              data: {
                message: this.translations['ticket.add_success'],
                type: 'success',
                delay: 3000,
              },
            },
            true,
          );
        }
        if (status.id === '5' && this.activeTerminalLayout === 'TicketCheck') {
          this.busService.sendMessage(
            {
              action: 'Tickets.Update',
              enforceEvent: true,
              data: {
                action: 'Payout',
                ticketData: ticket,
                showMessage: false,
              },
            },
            true,
          );
          if (this.terminalUserData.type === 'OPERATOR') {
            this.busService.sendMessage(
              {
                action: 'Peripherals.Print',
                enforceEvent: true,
                data: {
                  layoutName: 'ticketVirtualPenaltyShootoutPayout',
                  productId: 'VirtualPenaltyShootout',
                  data: ticket,
                },
              },
              true,
            );
            this.busService.sendMessage(
              {
                action: 'Dialog.Show',
                data: {
                  message: this.translations['ticket.payout_success'].replace('{{amount}}', payout.toFixed(2)),
                  type: 'success',
                  delay: 3000,
                },
              },
              true,
            );
          }
          if (this.terminalUserData.type !== 'OPERATOR') {
            this.busService.sendMessage(
              {
                action: 'Dialog.Show',
                data: {
                  message: this.translations['ticket.payout_success_to_balance'].replace(
                    '{{amount}}',
                    payout.toFixed(2),
                  ),
                  type: 'success',
                  delay: 3000,
                },
              },
              true,
            );
          }
          fetch(`${process.env.VUE_APP_TERMINAL_TICKET_CHECK_PATH}/${this.ticketDetailsData.id}.json`, {
            headers: {
              Authorization: `Basic ${this.getTicketDetailsAuth}`,
              'SEVEN-LOCALE': this.config.locale,
            },
          })
            .then((response) => response.text())
            .then((text) => this.setTicketDetailsData(JSON.parse(text)));
        }
        if (status.id === '1' && this.activeTerminalLayout === 'TicketCheck') {
          this.busService.sendMessage(
            {
              action: 'Tickets.Update',
              enforceEvent: true,
              data: {
                action: 'Cancel',
                ticketData: ticket,
                showMessage: false,
              },
            },
            true,
          );
          this.busService.sendMessage(
            {
              action: 'Dialog.Show',
              data: {
                message: this.translations['ticket.cancel_success_to_balance'].replace(
                  '{{amount}}',
                  cancelledAmount.toFixed(2),
                ),
                type: 'success',
                delay: 3000,
              },
            },
            true,
          );
          this.setTicketDetailsData(ticket);
        }
        if (code) {
          this.busService.sendMessage(
            {
              action: 'Dialog.Show',
              data: {
                message: eventDetail.message,
                type: 'warning',
                delay: 3000,
              },
            },
            true,
          );
          if (code === 7000) {
            this.setBetslipLockMessage(eventDetail.message);
          }
        }
        this.disable_betting(false);
        this.setScheduleDisabled(false);
        this.setIsPayinButtonDisabled(false);
        this.clearCheckTimeout();
        this.clearBetslip();
        this.setIsPayinInProgress(false);
      }
    },
    handleAuthorizationChanged(eventDetail) {
      const { auth, userType } = eventDetail.data;
      if (auth) {
        this.setUser(auth);
      }
      if (userType === 'OPERATOR' || userType === 'ANONYMOUS') {
        this.setTerminalUserData(eventDetail.data);
      }
      eventBus.$emit('authChange');
      if (this.userLogged) {
        this.setUserBalance(auth.user.balance);
      } else {
        this.busService.removeChannel(`${this.config.productName}.Player`, this.userUuid);
        this.setPlayerTickets([]);
        eventBus.$emit('closeTicketHistory');
        this.clearBetslip();
      }
      if (this.isNextGen) {
        this.updateUser({
          auth: eventDetail.data?.auth,
          profile: eventDetail.data?.auth?.user,
        });
      }
      this.set_loaded(true);
      this.setAuthChanged(true);
    },
    handleTicketsChecked(eventDetail) {
      const { ticket } = eventDetail.data;

      this.set_loaded(false);
      fetch(`${process.env.VUE_APP_TERMINAL_TICKET_CHECK_PATH}/${ticket.id}.json`, {
        headers: {
          Authorization: `Basic ${this.getTicketDetailsAuth}`,
          'SEVEN-LOCALE': this.config.locale,
        },
      })
        .then((response) => response.text())
        .then((text) => {
          const formated = JSON.parse(text);

          if (ticket.ticketPin) {
            formated.ticketPin = ticket.ticketPin;
          }

          this.setTicketDetailsData(formated);
        });
    },
  },
};
</script>

<style lang="scss">
:root {
  --text-primary-1: #efefef;
  --bg-color-2: #1b1c1d;
  --card-bg: #2c2e30;
  --modal-bg: #1a1c1d;
  --font-size-large: 16px;
  --betslip-bg: rgb(18, 19, 20);
  --loader-wrapper-bg: rgba(18, 19, 20, 0.7);
  --loader-color: var(--text-primary-1);
  --loader-color-active: rgba(239, 239, 239, 0.2);
  --background: hsl(180, 4%, 11%);
  --background-light: hsl(210, 4%, 18%);
  --game-header-background: hsla(225, 5%, 33%, 1);
  --background-3: hsl(210, 5%, 15%);
}
html,
body {
  zoom: 1 !important;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  color: var(--text-primary-1);
  //  height: 100%;
  scrollbar-width: none;
  //overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  ::-webkit-scrollbar {
    width: 0 !important;
  }
  -webkit-text-size-adjust: 100%; /* Prevent font scaling */
  text-size-adjust: 100%;
}

*,
*:after,
*:before {
  box-sizing: border-box;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

#app {
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  font-size: 14px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: #0c0b0c;
  margin: 0 auto;
  user-select: none;
  background-repeat: no-repeat;
  background-size: cover;
  scrollbar-width: 0 !important;
  ::-webkit-scrollbar {
    display: none;
  }
  &.hide-cursor {
    * {
      cursor: none !important;
    }
  }

  .cancel-ticket-confirm,
  .free-bet-expired-modal {
    .modal {
      height: 182px;
    }
  }

  .cancel-ticket-confirm-modal,
  .free-bet-modal,
  .free-bet-expired-modal {
    .modal {
      background-color: var(--card);
      height: 182px;

      .modal-header,
      .modal-body {
        color: var(--text-primary-1);
      }

      .modal-header {
        font-size: 16px;
        padding: 0 16px;
      }
      .modal-body {
        font-size: 14px;
        .modal-actions {
          display: none;
        }
      }
      .modal-footer {
        padding-right: 43px;
      }
      .button {
        background: transparent;
        border: 0;
        outline: 0;
        font-size: 14px;
        color: #ffc107;
        text-transform: uppercase;
        cursor: pointer;
        font-family: Roboto, 'sans-serif';
        padding: 31px 0 27px 0;
      }
    }
  }

  .free-bet-modal {
    .modal {
      .modal-header {
        display: none;
      }
      .modal-body {
        height: 348px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-position: 0 0;
        background-repeat: no-repeat;
        position: relative;
        text-align: center;
        .body {
          height: 100%;
          img {
            position: absolute;
            top: -2%;
            left: 22px;
            max-width: 100%;
          }
          .body-message-box {
            position: absolute;
            width: 100%;
            top: 66%;
            left: 50%;
            transform: translate(-50%, -50%);

            .title {
              font-size: 42px;
              font-weight: 700;
              color: #90b653;
              margin-bottom: 25px;
            }
            .subtitle {
              font-size: 18px;
              font-weight: 700;
            }
            .subtitle-2 {
              font-size: 14px;
              color: var(--text-primary-3);
            }
          }
        }
      }
      .modal-footer {
        display: none;
      }
    }
    &.desktop {
      .modal {
        .modal-body {
          background-image: none;
          .body {
            img {
              height: 60%;
              left: 50%;
              top: -2%;
              transform: translate(-50%, 0);
            }
            .body-message-box {
              top: 60%;
              .title {
                margin-bottom: 10px;
              }
              .pre-title {
                display: none;
              }
            }
            .action {
              position: absolute;
              bottom: 0;
              right: 16px;
            }
          }
        }
      }
    }
    &.mobile {
      .modal {
        .modal-body {
          background-image: url('assets/free-bet-bg.svg');
          .body {
            .body-message-box {
              .title {
                text-transform: uppercase;
              }
            }
            .action {
              position: absolute;
              left: 0;
              bottom: 20px;
              display: flex;
              width: 100%;
              justify-content: center;
              align-items: center;
              .button {
                min-width: 150px;
                background: #90b653;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 30px;
                padding: 0;
                color: var(--text-secondary-1);
                font-weight: 500;
              }
            }
          }
        }
      }
    }
  }
}
.modal-back.cancel-ticket {
  .modal.show-modal {
    padding: 16px !important;
    .modal-header {
      height: unset;
      padding-top: 8px;
      padding-bottom: 24px;
      font-size: 14px;
      h3 {
        margin: 0 !important;
        font-size: 18px !important;
        text-align: left;
      }
    }
    .modal-body {
      font-size: 14px !important;
      text-align: left;
      padding-bottom: 40px;
    }
    .modal-footer {
      padding: 0;
      height: unset;
      padding-bottom: 14px;
    }
  }
  .modal.popup-show-modal {
    padding: 24px !important;
    .modal-header {
      padding-left: 0px !important;
      h3 {
        margin: 0 !important;
        font-size: 18px !important;
      }
    }
    .modal-body {
      font-size: 16px !important;
    }
  }
}

@media all and (max-width: 420px) {
  .modal-back.ticket-details-modal {
    .modal.show-modal-mobile {
      background-color: var(--background-1) !important;
    }
  }
  .modal-back.cancel-ticket {
    padding: 0px;
    .modal.show-modal {
      padding: 24px !important;
      .modal-header {
        h3 {
          margin: 0 !important;
          font-size: 18px !important;
        }
      }
      .modal-body {
        font-size: 16px !important;
      }
    }
    .modal.popup-show-modal {
      padding: 24px !important;
      background-color: var(--card) !important;
      .modal-header {
        padding-left: 0px !important;
        padding-bottom: 0px !important;
        h3 {
          margin: 0 !important;
          font-size: 18px !important;
        }
      }
      .modal-body {
        font-size: 16px !important;
        padding: 8px 0px 0px 0px !important;
      }
      .modal-footer {
        padding: 0px 0px 0px 30px !important;
      }
    }
  }
}

@media (max-width: 640px) {
  .app {
    .cancel-ticket-confirm-modal,
    .free-bet-expired-modal {
      background-color: var(--overlay-lock);
      .modal {
        background-color: var(--card);
        color: var(--text-primary-1);
        height: 182px !important;

        .modal-header {
          background-color: var(--card);
          padding: 0 24px 16px 24px !important;
        }

        .modal-body {
          position: relative;
          padding: 0 24px;
          color: var(--text-primary-1);
          height: 40px;
          .modal-actions {
            display: block !important;
            position: absolute;
            right: 0;
            padding-right: 43px;
          }
        }
      }
    }

    .free-bet-expired-modal {
      .modal {
        max-height: 182px !important;
      }
    }
    .free-bet-modal {
      .modal {
        max-height: 70% !important;
        .modal-body {
          height: 100% !important;
        }
      }
    }
  }
}
</style>
